import { MoreHoriz } from "@mui/icons-material";
import {
  AspectRatio,
  Box,
  Card,
  CardContent,
  CardOverflow,
  Chip,
  Dropdown,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  Typography,
} from "@mui/joy";
import { SxProps } from "@mui/joy/styles/types";
import { useMatches, useMatchRoute, useNavigate } from "@tanstack/react-router";
import { format, isFuture } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { ApiCompetition, ApiLeague, ApiLeagueSummary } from "griptonite-api-client/types";
import { ComponentProps } from "react";

import { useCurrentVenueQuery, useCurrentVenueUuid } from "@/hooks/venues";
import getThumbnailUrl from "@/utils/getThumbnailUrl";

import formatPeriod from "../utils/formatPeriod";
import { CardLink } from "./Links";
import { useModal } from "./modals/useModal";

const colorsMap = {
  ACTIVE: "#1F9A00",
  UPCOMING: "#0075FF",
  ENDED: "#FD5348",
  PENDING_RESULTS: "#fc9003",
};

export const getCompetitionStatusLabel = (status: ApiCompetition["status"]) => {
  const label = {
    ACTIVE: "Active",
    UPCOMING: "Upcoming",
    ENDED: "Ended",
    PENDING_RESULTS: "Pending Results",
  }[status];
  return label;
};

export const CompetitionStatusChip = ({
  status,
  ...props
}: {
  status: ApiCompetition["status"];
} & ComponentProps<typeof Chip>) => {
  const label = getCompetitionStatusLabel(status);
  return (
    <Chip
      sx={{ color: colorsMap[status], fontWeight: "bold", ...props.sx }}
      variant="plain"
      size="md"
      {...props}
    >
      {label}
    </Chip>
  );
};

export const CompetitionCard = ({
  competition,
  creatingEventType,
}: {
  competition?: ApiCompetition;
  creatingEventType?: "competition" | "league" | "round";
}) => {
  const currentVenueUuid = useCurrentVenueUuid();

  const { data: venue } = useCurrentVenueQuery();

  const isActive = useMatchRoute()({
    to: `/gym/$venueUuid/events/$competitionUuid`,
    params: {
      venueUuid: currentVenueUuid,
      competitionUuid: competition?.uuid,
    },
    fuzzy: true,
  });
  const matches = useMatches();

  let tab: "profile" | "routes" | "competitors" = "profile";
  const currentRoute = matches[matches.length - 1];
  if (currentRoute?.routeId.startsWith("/gym/$venueUuid/events/$competitionUuid/routes")) {
    tab = "routes";
  } else if (
    currentRoute?.routeId.startsWith("/gym/$venueUuid/events/$competitionUuid/competitors")
  ) {
    tab = "competitors";
  }

  const { display: displayModal } = useModal();

  const cardSx = {
    borderColor: isActive ? "primary.blue2" : "neutral.200",
    borderWidth: isActive ? "2px" : "1px",
    textDecoration: "none",
  };
  const content = (
    <>
      <CardOverflow>
        <AspectRatio ratio="2" objectFit="cover">
          {competition?.coverImageUrl ? (
            <img
              src={
                getThumbnailUrl(competition.coverImageUrl, {
                  size: 600,
                  mode: "resize",
                }) ?? undefined
              }
            />
          ) : null}
        </AspectRatio>
        {competition ? (
          <Dropdown>
            <MenuButton
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                borderRadius: "100%",
              }}
              slots={{
                root: IconButton,
              }}
              slotProps={{
                root: {
                  size: "sm",
                  color: "neutral",
                  variant: "soft",
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <MoreHoriz />
            </MenuButton>
            <Menu size="sm">
              <MenuItem
                color="danger"
                onClick={(e) => {
                  displayModal("CONFIRM_DELETE_COMPETITION", { competition });
                  e.preventDefault();
                }}
              >
                Delete
              </MenuItem>
            </Menu>
          </Dropdown>
        ) : null}
      </CardOverflow>
      <CardContent sx={{ position: "relative" }}>
        {competition && competition.venues[0].uuid !== currentVenueUuid ? (
          <Box
            sx={{
              position: "absolute",
              top: 2,
              right: 8,
              display: "flex",
              backgroundColor: "neutral.100",
              borderRadius: 6,
              zIndex: 1,
              transform: "translateY(-100%)",
              background: "white",
              padding: 0.25,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                overflow: "hidden",
                borderRadius: 4,
                width: 40,
                height: 40,
              }}
            >
              <img
                src={competition!.venues[0].logoUrl ?? undefined}
                alt={competition!.venues[0].name}
                style={{ width: 40, height: 40 }}
              ></img>
            </Box>
          </Box>
        ) : null}
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography level="title-md" fontWeight="bold">
            {competition?.name ||
              (creatingEventType === "league"
                ? "New series"
                : creatingEventType === "round"
                  ? "New round"
                  : "New competition")}
          </Typography>
          <CompetitionStatusChip status={competition?.status || "UPCOMING"} />
        </Box>
        <Typography textColor="neutral.500" level="body-sm">
          {competition ? formatPeriod(competition!, venue?.timezone || "UTC") : null}
        </Typography>
      </CardContent>
    </>
  );

  if (!competition) {
    return <Card sx={cardSx}>{content}</Card>;
  }
  return (
    <CardLink
      data-uuid={competition.uuid}
      to={`/gym/$venueUuid/events/$competitionUuid/${tab}`}
      params={{
        venueUuid: competition.venues[0].uuid,
        competitionUuid: competition.uuid,
      }}
      sx={cardSx}
    >
      {content}
    </CardLink>
  );
};

export const LeagueCard = ({
  league,
  sx,
}: {
  league: ApiLeagueSummary | ApiLeague;
  sx?: SxProps;
}) => {
  const currentVenueUuid = useCurrentVenueUuid();
  const endsInFuture = league.endTime ? isFuture(league.endTime) : true;
  const { data: currentVenue } = useCurrentVenueQuery();

  const navigate = useNavigate();
  const { display: displayModal } = useModal();

  const isActive = useMatchRoute()({
    to: `/gym/$venueUuid/events/series/$leagueUuid`,
    params: {
      venueUuid: currentVenueUuid,
      leagueUuid: league.uuid,
    },
    fuzzy: true,
  });

  const hasEmptyCategories = "categories" in league && league.categories.length === 0;

  return (
    <CardLink
      data-uuid={league.uuid}
      from="/gym/$venueUuid"
      to="/gym/$venueUuid/events/series/$leagueUuid"
      params={(prev) => ({ ...prev, leagueUuid: league.uuid })}
      size="sm"
      sx={{
        borderColor: isActive ? "primary.blue2" : "neutral.200",
        borderWidth: isActive ? "2px" : "1px",
        textDecoration: "none",
        overflow: "hidden",
        ...sx,
      }}
    >
      <CardOverflow>
        <AspectRatio ratio="2" objectFit="cover">
          {league.coverImageUrl ? (
            <img
              src={
                getThumbnailUrl(league.coverImageUrl, {
                  size: 600,
                  mode: "resize",
                }) ?? undefined
              }
            />
          ) : null}
        </AspectRatio>
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            borderBottomRightRadius: 8,
            padding: 0.5,
            paddingX: 1,
            fontWeight: "bold",
            color: "white",
            backgroundColor: "#000a",
            fontSize: "md",
          }}
        >
          Series
        </Box>
        <Dropdown>
          <MenuButton
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              borderRadius: "100%",
            }}
            slots={{
              root: IconButton,
            }}
            slotProps={{
              root: {
                size: "sm",
                color: "neutral",
                variant: "soft",
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <MoreHoriz />
          </MenuButton>
          <Menu size="sm">
            <MenuItem
              onClick={(e) => {
                navigate({
                  from: `/gym/$venueUuid/`,
                  to: `/gym/$venueUuid/events/create`,
                  search: {
                    series: league.uuid,
                  },
                });
                e.preventDefault();
              }}
              disabled={hasEmptyCategories}
            >
              Add round
            </MenuItem>
            <MenuItem
              color="danger"
              onClick={(e) => {
                displayModal("CONFIRM_DELETE_LEAGUE", {
                  leagueUuid: league.uuid,
                });
                e.preventDefault();
              }}
            >
              Delete
            </MenuItem>
          </Menu>
        </Dropdown>
      </CardOverflow>
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography level="title-md" fontWeight="bold">
            {league.name}
          </Typography>
          <CompetitionStatusChip status={league.status} />
        </Box>
        {league.endTime ? (
          <Typography textColor="neutral.500" level="body-sm">
            {endsInFuture ? "Ends on " : "Ended on "}
            {format(
              toZonedTime(league.endTime, currentVenue?.timezone || "UTC"),
              "do MMM 'at' hh:mm",
            )}
          </Typography>
        ) : null}
      </CardContent>
    </CardLink>
  );
};
